import React, { useState } from 'react';
import { OperationMS, StatusId } from '../types';
import OperationAnalyseAccordion from './OperationAnalyseAccordion';
import Banner from '../../../composants/Banner/Banner';
import Button from '../../../composants/Button/Button';
import { prolongerDelai } from '../services/operationMsService';
import { getProlongations } from '../domain/analyse/historique';
import { formatDateHeure } from '../domain/analyse/dates';

const BANNIERE =
  "Il est possible de changer la date de réception des offres une fois la consultation lancée. Il convient " +
  "toutefois de le faire savoir dans les Questions/Réponses à tous les candidats pour s'assurer qu'ils ont " +
  'bien pris en compte le changement.';

// Convertit une date/ISO en "YYYY-MM-DDTHH:mm" (heure locale) pour <input type="datetime-local">.
const toDateTimeLocal = (v?: string | null): string => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface OperationProlongationDelaiProps {
  operation: OperationMS;
  onProlonged: () => void;
}

// Section "Prolongation de délai" de l'onglet Consultation. Verrouillee (cadenas, calquee sur les sections
// verrouillees de l'onglet Analyse) tant que la consultation n'est pas lancee (statut < 2). Une fois lancee :
// modifie la date de reception des offres (update expire_at + date_limite_remise cote back) et affiche
// l'historique des prolongations (date/heure + operateur), source = evenements 'Prolongation' de l'historique.
const OperationProlongationDelai: React.FC<OperationProlongationDelaiProps> = ({ operation, onProlonged }) => {
  // La prolongation n'est possible QUE pendant la consultation (statut EXACT ConsultationEnCours = 2) :
  // verrouillee avant (brouillon) ET apres (analyse / signature / cloturee...).
  const statusId = operation.status?.id ?? 0;
  const enConsultation = statusId === StatusId.ConsultationEnCours;
  const lockedHint =
    statusId < StatusId.ConsultationEnCours
      ? 'Disponible dès la consultation lancée'
      : "La consultation est terminée : le délai n'est plus modifiable";
  const deadlineActuelle = operation.date_limite_remise ?? operation.expire_at;
  // Borne mini = maintenant : impossible de choisir une date/heure ANTERIEURE (date ET heure).
  const minDateTime = toDateTimeLocal(new Date().toISOString());
  const [nouvelleDate, setNouvelleDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const prolongations = getProlongations(operation.historique);

  const submit = async (): Promise<void> => {
    if (!nouvelleDate) {
      setError('Sélectionnez une nouvelle date et heure de réception des offres.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await prolongerDelai(operation.id, nouvelleDate);
      setNouvelleDate('');
      onProlonged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la prolongation du délai.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OperationAnalyseAccordion
      title="Prolongation de délai"
      locked={!enConsultation}
      lockedKeepOpen
      lockedHint={lockedHint}
    >
      <Banner variant="info" message={BANNIERE} />

      {enConsultation && (
        // opms__descriptif-admin : herite EXACTEMENT le style des champs date du Descriptif procedural.
        <div className="opms__prolongation opms__descriptif-admin">
          <div className="form-group">
            <label>Date et heure de réception des offres actuelle</label>
            <div className="input-inactif">{formatDateHeure(deadlineActuelle) ?? '—'} (GMT Paris)</div>
          </div>

          <div className="form-group">
            <label>Nouvelle date et heure de réception des offres</label>
            {/* "(GMT Paris)" DANS le champ (meme pattern que le Descriptif procedural). */}
            <div className="opms__datetime-gmt">
              <input
                type="datetime-local"
                value={nouvelleDate || toDateTimeLocal(deadlineActuelle)}
                min={minDateTime}
                onChange={(e) => {
                  const v = e.target.value;
                  // Rejet des date-heures anterieures a maintenant (date ET heure) : filet en plus du `min` natif.
                  setNouvelleDate(v && v < minDateTime ? minDateTime : v);
                }}
              />
              <span className="opms__datetime-gmt-suffix">(GMT Paris)</span>
            </div>
          </div>

          {error && <span className="error-message">{error}</span>}

          <div className="opms__prolongation-action">
            <Button
              value={saving ? 'Enregistrement...' : 'Prolonger le délai'}
              variant="primary"
              onClick={submit}
              disabled={saving || !nouvelleDate}
            />
          </div>

          {prolongations.length > 0 && (
            <div className="opms__prolongation-historique">
              <h4>Historique des modifications</h4>
              <ul>
                {prolongations.map((p, i) => (
                  <li key={i}>
                    {formatDateHeure(p.date) ?? '—'}
                    {p.operateur ? ` - ${p.operateur}` : ''}
                    {p.message ? ` : ${p.message}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </OperationAnalyseAccordion>
  );
};

export default OperationProlongationDelai;
